<script setup>
import { ref } from 'vue';
import { makePledge } from "@/services/api_services";
import {useRoute, useRouter} from "vue-router";

const amount = ref('');
const phoneNumber = ref('');
const name = ref("");

const loading = ref(false);
const error = ref(null);
const showSuccessDialog = ref(false);

const route = useRoute();
const fundraiserIdentifier = route.params.fundraiserIdentifier;
const fundraiserName = route.params.fundraiserName;
const fundraiserId = route.params.fundraiserId;
const router = useRouter();


const handleMakePledge = async () => {
  error.value = null;

  if (!amount.value) {
    error.value = 'Amount is required.';
    return;
  }
  if (!phoneNumber.value) {
    error.value = 'Phone number is required.';
    return;
  }
  if (!name.value) {
    error.value = 'Name is required.';
    return;
  }

  loading.value = true;

  try {
    const response = await makePledge(
        name.value,
        amount.value,
        phoneNumber.value,
        fundraiserIdentifier,
        fundraiserId
    );

    if (response?.result) {
      showSuccessDialog.value = true;
    } else {
      error.value = 'Failed to make pledge. Please try again.';
    }
  } catch (e) {
    console.error('Pledge error:', e);
    error.value = e.response?.data?.message || 'Failed to make pledge. Please try again.';
  } finally {
    loading.value = false;
  }
};

function goToMpesaContributionPage(fundraiserId,fundraiserName,fundraiserIdentifier) {
  router.push({
    name: 'MpesaContribution',
    params: { fundraiserId: fundraiserId, fundraiserName: fundraiserName, fundraiserIdentifier: fundraiserIdentifier},
  })}
function goToPayStackContributionPage(fundraiserId,fundraiserName,fundraiserIdentifier) {
  router.push({ name: 'PayStackContribution',    params: { fundraiserId: fundraiserId, fundraiserName: fundraiserName, fundraiserIdentifier: fundraiserIdentifier} });
}
</script>

<template>
  <div class="page-container">
    <div class="contribution-form-container">
      <h1 class="page-title">Make pledge for {{ fundraiserName }}</h1>
      <p class="form-instructions">Fill out the form below to make a pledge</p>

      <div class="form-group">
        <label for="name">Full Name</label>
        <input
            id="name"
            v-model="name"
            type="text"
            placeholder="Enter your full name"
            class="form-input"
        />
      </div>

      <div class="form-group">
        <label for="amount">How much would you like to pledge?</label>
        <input
            id="amount"
            v-model="amount"
            type="number"
            placeholder="Enter amount (KES)"
            min="1"
            class="form-input"
        />
      </div>

      <div class="form-group">
        <label for="phone">Phone Number</label>
        <input
            id="phone"
            v-model="phoneNumber"
            type="tel"
            placeholder="e.g., 07XXXXXXXX"
            class="form-input"
        />
      </div>

      <button
          class="submit-button"
          :disabled="loading"
          @click="handleMakePledge"
          :class="{ 'button-loading': loading }"
      >
        <span v-if="!loading">Make Pledge</span>
        <span v-else class="loading-text">Processing...</span>
      </button>

      <div v-if="error" class="message error-message">
        <svg class="icon" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
        </svg>
        {{ error }}
      </div>

      <div class="security-notice">
        <svg class="lock-icon" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd" />
        </svg>
        <span>Your information is secure and will not be shared</span>
      </div>
    </div>

    <Teleport to="body">
      <div v-if="showSuccessDialog" class="dialog-overlay" @click.self="showSuccessDialog = false">
        <div class="dialog">
          <div class="dialog-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10" />
              <polyline points="9 12 11 14 15 10" />
            </svg>
          </div>

          <h2 class="dialog-title">Pledge Received!</h2>
          <p class="dialog-message">
            Pledge has been successfully received, please click here to start redeeming your pledge.
          </p>

          <div class="dialog-buttons">
            <button class="give-button mpesa-button" @click="goToMpesaContributionPage(fundraiserId,fundraiserName,fundraiserIdentifier)">
              <svg class="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="2" y="5" width="20" height="14" rx="2" />
                <line x1="2" y1="10" x2="22" y2="10" />
              </svg>
              Give Now (M-Pesa)
            </button>

            <button class="give-button paystack-button" @click="goToPayStackContributionPage(fundraiserId, fundraiserName, fundraiserIdentifier)">
              <svg class="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="2" y="5" width="20" height="14" rx="2" />
                <line x1="2" y1="10" x2="22" y2="10" />
              </svg>
              Give Now (Other Payment Methods)
            </button>
          </div>

        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.page-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: linear-gradient(
      135deg,
      rgba(203, 217, 196, 1) 0%,
      rgba(249, 247, 194, 1) 50%,
      rgba(249, 213, 206, 1) 100%
  );
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.contribution-form-container {
  width: 100%;
  max-width: 480px;
  padding: 2.5rem;
  background: rgba(255, 255, 255, 0.96);
  border-radius: 16px;
  box-shadow:
      0 12px 24px rgba(0, 0, 0, 0.08),
      0 4px 12px rgba(0, 0, 0, 0.05);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.page-title {
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
  text-align: center;
  color: #111827;
  line-height: 1.3;
}

.form-instructions {
  text-align: center;
  margin-bottom: 2rem;
  color: #6b7280;
  font-size: 0.95rem;
}

.form-group {
  margin-bottom: 1.75rem;
}

label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #374151;
  font-size: 0.95rem;
}

.form-input {
  width: 100%;
  padding: 0.95rem 1.35rem;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background-color: #f9fafb;
  box-sizing: border-box;
}

.form-input:focus {
  border-color: #10b981;
  outline: none;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
  background-color: white;
}

.form-input::placeholder {
  color: #9ca3af;
}

.submit-button {
  width: 100%;
  padding: 1.1rem;
  margin-top: 0.5rem;
  background: linear-gradient(to right, #10b981, #059669);
  color: white;
  font-weight: 600;
  font-size: 1rem;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.submit-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(16, 185, 129, 0.2);
}

.submit-button:disabled {
  opacity: 0.8;
  cursor: not-allowed;
}

.button-loading { color: transparent; }

.loading-text {
  position: absolute;
  left: 0;
  right: 0;
  color: white;
}

.message {
  padding: 0.85rem 1rem;
  border-radius: 8px;
  margin-top: 1.5rem;
  display: flex;
  align-items: center;
  font-size: 0.95rem;
}

.error-message {
  background-color: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
}

.icon {
  width: 1.25rem;
  height: 1.25rem;
  margin-right: 0.75rem;
  flex-shrink: 0;
}

.security-notice {
  display: flex;
  align-items: center;
  margin-top: 1.5rem;
  font-size: 0.85rem;
  color: #6b7280;
  padding: 0.75rem;
  background-color: #f8fafc;
  border-radius: 8px;
}

.lock-icon {
  width: 1rem;
  height: 1rem;
  margin-right: 0.5rem;
  color: #6b7280;
}

/* ── Dialog ── */
.dialog-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1.5rem;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to   { opacity: 1; }
}

.dialog {
  background: #ffffff;
  border-radius: 20px;
  padding: 2.25rem 2rem;
  width: 100%;
  max-width: 400px;
  text-align: center;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.18);
  animation: slideUp 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

@keyframes slideUp {
  from { transform: translateY(30px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
}

.dialog-icon {
  width: 68px;
  height: 68px;
  border-radius: 50%;
  background: #ecfdf5;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.25rem;
}

.dialog-icon svg {
  width: 36px;
  height: 36px;
  stroke: #10b981;
}

.dialog-title {
  font-size: 1.35rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.65rem;
}

.dialog-message {
  font-size: 0.95rem;
  color: #4b5563;
  line-height: 1.6;
  margin-bottom: 1.75rem;
}

.dialog-buttons {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.give-button {
  width: 100%;
  padding: 0.9rem 1rem;
  border: none;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
}

.give-button:hover {
  transform: translateY(-1px);
}

.give-button:active {
  transform: scale(0.98);
}

.btn-icon {
  width: 18px;
  height: 18px;
}

.mpesa-button {
  background: linear-gradient(to right, #10b981, #059669);
  color: white;
}

.mpesa-button:hover {
  box-shadow: 0 6px 16px rgba(16, 185, 129, 0.3);
}

.paystack-button {
  background: linear-gradient(to right, #011B33, #01305c);
  color: white;
}

.paystack-button:hover {
  box-shadow: 0 6px 16px rgba(1, 27, 51, 0.3);
}

.dialog-close {
  background: none;
  border: none;
  color: #9ca3af;
  font-size: 0.875rem;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  transition: color 0.2s;
}

.dialog-close:hover {
  color: #6b7280;
}

@media (max-width: 600px) {
  .page-container { padding: 1.25rem; }
  .contribution-form-container { padding: 1.75rem; }
  .page-title { font-size: 1.5rem; }
  .dialog { padding: 1.75rem 1.5rem; }
}
</style>